import React, { useId } from 'react'
import camelCase from 'camelcase'
import { Chip, Loader, Modal, Notification } from 'is24-corecss'
import { useTranslation } from 'react-i18next'
import { FlowMetaData } from '../../../../@types/types'
import { useListingTypeChangeSelection } from './useListingTypeChangeSelection'
import styles from './ListingTypeChangeModal.module.css'

type ListingTypeChangeModalProps = {
  flowMetadata: FlowMetaData
  isOpen: boolean
  isSubmitting: boolean
  onClose: () => void
  onSubmit: (target: NonNullable<ReturnType<typeof useListingTypeChangeSelection>['target']>) => void
  submissionError?: boolean
}

export const ListingTypeChangeModal = ({
  flowMetadata,
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
  submissionError = false,
}: ListingTypeChangeModalProps) => {
  const { t } = useTranslation()
  const loadingTitleId = useId()
  const {
    availableBusinessTypes,
    availableRealEstateTypes,
    availableTransactions,
    canProceed,
    selectedBusinessType,
    selectedRealEstate,
    selectedTransaction,
    selectBusinessType,
    selectRealEstate,
    selectTransaction,
    target,
  } = useListingTypeChangeSelection(flowMetadata)

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  return (
    <Modal.Root isOpen={isOpen} onClose={handleClose}>
      <Modal.Backdrop>
        <Modal.Surface aria-labelledby={isSubmitting ? loadingTitleId : undefined} width={isSubmitting ? 500 : 600}>
          {isSubmitting ? (
            <Modal.Body>
              <div className={styles.loadingState} role='status'>
                <Loader variant='secondary' size='medium' colorSet='darkGray' />
                <div className={styles.loadingCopy}>
                  <h2 className='font-heading-xsmall-bold' id={loadingTitleId}>
                    {t('listingTypeChange.modal.loadingTitle')}
                  </h2>
                  <p className='font-body-medium-regular'>{t('listingTypeChange.modal.loadingDescription')}</p>
                </div>
              </div>
            </Modal.Body>
          ) : (
            <>
              <Modal.Header>
                <Modal.TitleGroup>
                  <Modal.Title>{t('listingTypeChange.modal.title')}</Modal.Title>
                </Modal.TitleGroup>
                <Modal.CloseButton aria-label={t('button.close')} onClick={handleClose} />
              </Modal.Header>
              <Modal.Body>
                <p className='font-body-large-regular margin-bottom-l'>{t('listingTypeChange.modal.description')}</p>

                <section className='margin-bottom-l'>
                  <span className='font-body-medium-bold'>{t('listingTypeChange.modal.transactionLabel')}</span>
                  <div className='grid-flex gutter-s margin-top-xs' role='radiogroup'>
                    {availableTransactions.map((transaction) => (
                      <div className='grid-item' key={transaction}>
                        <Chip.Root
                          active={selectedTransaction === transaction}
                          data-testid={`listing-type-change-transaction-${transaction.toLowerCase().replace(/_/g, '-')}`}
                          disabled={isSubmitting}
                          onChange={() => selectTransaction(transaction)}
                          size='small'
                        >
                          <Chip.Label>{t(`segmentation.transactionType.${camelCase(transaction)}`)}</Chip.Label>
                        </Chip.Root>
                      </div>
                    ))}
                  </div>
                </section>

                {selectedTransaction && selectedTransaction !== 'SHORT_TERM_ACCOMMODATION' && (
                  <section className='margin-bottom-l'>
                    <span className='font-body-medium-bold'>{t('listingTypeChange.modal.realEstateTypeLabel')}</span>
                    <div className='grid-flex gutter-s margin-top-xs' role='radiogroup'>
                      {availableRealEstateTypes.map((realEstate) => (
                        <div className='grid-item' key={realEstate}>
                          <Chip.Root
                            active={selectedRealEstate === realEstate}
                            data-testid={`listing-type-change-real-estate-${realEstate.toLowerCase().replace(/_/g, '-')}`}
                            disabled={isSubmitting}
                            onChange={() => selectRealEstate(realEstate)}
                            size='small'
                          >
                            <Chip.Label>{t(`segmentation.category.${camelCase(realEstate)}`)}</Chip.Label>
                          </Chip.Root>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {selectedRealEstate === 'BUSINESS' && (
                  <section className='margin-bottom-l'>
                    <span className='font-body-medium-bold'>{t('listingTypeChange.modal.businessTypeLabel')}</span>
                    <div className='grid-flex gutter-s margin-top-xs' role='radiogroup'>
                      {availableBusinessTypes.map((businessType) => (
                        <div className='grid-item' key={businessType}>
                          <Chip.Root
                            active={selectedBusinessType === businessType}
                            data-testid={`listing-type-change-business-${businessType.toLowerCase().replace(/_/g, '-')}`}
                            disabled={isSubmitting}
                            onChange={() => selectBusinessType(businessType)}
                            size='small'
                          >
                            <Chip.Label>{t(`segmentation.businessCategory.${camelCase(businessType)}`)}</Chip.Label>
                          </Chip.Root>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {selectedTransaction === 'SHORT_TERM_ACCOMMODATION' && (
                  <Notification.Root className='margin-bottom-l' type='info' variant='flat'>
                    <Notification.Icon />
                    <Notification.Content>
                      <Notification.Message>{t('segmentation.hintShortTermAccommodation')}</Notification.Message>
                    </Notification.Content>
                  </Notification.Root>
                )}

                {submissionError && (
                  <Notification.Root data-testid='listing-type-change-error' type='error' variant='flat'>
                    <Notification.Icon />
                    <Notification.Content>
                      <Notification.Message>{t('listingTypeChange.modal.error')}</Notification.Message>
                    </Notification.Content>
                  </Notification.Root>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Modal.PrimaryFooterButton
                  data-testid='listing-type-change-submit'
                  disabled={!canProceed}
                  label={t('button.save.short')}
                  onClick={() => target && onSubmit(target)}
                  variant='filledDefault'
                />
              </Modal.Footer>
            </>
          )}
        </Modal.Surface>
      </Modal.Backdrop>
    </Modal.Root>
  )
}
